import React, { useEffect, useRef, useState } from "react";
import {
  IoCameraOutline,
  IoCheckmark,
  IoClose,
  IoImagesOutline,
  IoRefreshOutline,
} from "react-icons/io5";

type ContinuousCameraProps = {
  initialPhotoCount: number;
  onAccept: (blob: Blob) => Promise<void>;
  onClose: () => void;
  onFallback: () => void;
};

const captureFrame = (video: HTMLVideoElement): Promise<Blob> => {
  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  if (!sourceWidth || !sourceHeight) {
    return Promise.reject(new Error("Camera is not ready"));
  }

  const maximumEdge = 1920;
  const scale = Math.min(1, maximumEdge / Math.max(sourceWidth, sourceHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("Photo capture is unavailable"));
  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Photo capture failed"))),
      "image/jpeg",
      0.88
    )
  );
};

function ContinuousCamera({
  initialPhotoCount,
  onAccept,
  onClose,
  onFallback,
}: ContinuousCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const initialCountRef = useRef(initialPhotoCount);
  const historyEntryActive = useRef(false);
  const sessionPreviews = useRef<string[]>([]);
  const reviewUrlRef = useRef("");
  const [reviewBlob, setReviewBlob] = useState<Blob | null>(null);
  const [reviewUrl, setReviewUrl] = useState("");
  const [acceptedPreviews, setAcceptedPreviews] = useState<string[]>([]);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const sessionUrls = sessionPreviews.current;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraReady(true);
      } catch {
        if (active) setCameraError("Camera access is unavailable.");
      }
    };

    window.history.pushState({ bosscarContinuousCamera: true }, "");
    historyEntryActive.current = true;
    const handleBack = () => {
      historyEntryActive.current = false;
      onClose();
    };
    window.addEventListener("popstate", handleBack);
    void start();

    return () => {
      active = false;
      window.removeEventListener("popstate", handleBack);
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (historyEntryActive.current) {
        historyEntryActive.current = false;
        window.history.back();
      }
      sessionUrls.forEach((url) => URL.revokeObjectURL(url));
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    };
    // Camera lifetime intentionally matches this component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    const url = reviewBlob ? URL.createObjectURL(reviewBlob) : "";
    reviewUrlRef.current = url;
    setReviewUrl(url);
  }, [reviewBlob]);

  const takePhoto = async () => {
    if (!videoRef.current || !cameraReady) return;
    try {
      setReviewBlob(await captureFrame(videoRef.current));
    } catch (error: any) {
      setCameraError(error?.message || "Could not take this photo.");
    }
  };

  const acceptPhoto = async () => {
    if (!reviewBlob || saving) return;
    setSaving(true);
    try {
      await onAccept(reviewBlob);
      const preview = URL.createObjectURL(reviewBlob);
      sessionPreviews.current.push(preview);
      setAcceptedPreviews((current) => [...current, preview]);
      setReviewBlob(null);
    } catch (error: any) {
      setCameraError(error?.message || "Could not save this photo.");
    } finally {
      setSaving(false);
    }
  };

  const closeCamera = () => onClose();

  return (
    <div className="fixed inset-0 z-[100] bg-black text-white flex flex-col">
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
        <button className="h-11 w-11 rounded-full bg-black/55 flex items-center justify-center" onClick={closeCamera} aria-label="Close camera">
          <IoClose size={28} />
        </button>
        <strong className="rounded-full bg-black/55 px-4 py-2 text-sm">
          {initialCountRef.current + acceptedPreviews.length} photos
        </strong>
        <button className="h-11 px-4 rounded-full bg-white text-black font-bold" onClick={closeCamera}>
          Done
        </button>
      </div>

      <div className="relative flex-1 min-h-0 overflow-hidden">
        <video ref={videoRef} autoPlay muted playsInline className="absolute inset-0 h-full w-full object-cover" />

        {cameraError ? (
          <div className="absolute inset-0 z-10 bg-black flex flex-col items-center justify-center gap-4 px-8 text-center">
            <IoCameraOutline size={48} />
            <p>{cameraError}</p>
            <button className="h-12 rounded-full bg-white text-black px-6 font-bold flex items-center gap-2" onClick={onFallback}>
              <IoImagesOutline size={20} /> Use phone camera
            </button>
            <button className="text-white/80" onClick={closeCamera}>Cancel</button>
          </div>
        ) : null}

        {reviewUrl ? (
          <div className="absolute inset-0 z-10 bg-black flex flex-col">
            <img src={reviewUrl} alt="Review captured car" className="flex-1 min-h-0 w-full object-contain" />
            <div className="grid grid-cols-2 gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-black">
              <button className="h-14 rounded-full border border-white/40 font-bold flex items-center justify-center gap-2" onClick={() => setReviewBlob(null)} disabled={saving}>
                <IoRefreshOutline size={22} /> Retake
              </button>
              <button className="h-14 rounded-full bg-white text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50" onClick={() => void acceptPhoto()} disabled={saving}>
                <IoCheckmark size={24} /> {saving ? "Saving..." : "Use photo"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {!reviewUrl && !cameraError ? (
        <div className="relative bg-black px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="h-16 overflow-x-auto flex items-center gap-2 pr-24">
            {acceptedPreviews.map((url, index) => (
              <img key={`${url}-${index}`} src={url} alt="Accepted car" className="h-14 w-14 rounded-md object-cover border border-white/30" />
            ))}
          </div>
          <button
            className="absolute right-1/2 translate-x-1/2 bottom-[max(1.25rem,env(safe-area-inset-bottom))] h-16 w-16 rounded-full border-4 border-white bg-white/25 disabled:opacity-40"
            onClick={() => void takePhoto()}
            disabled={!cameraReady}
            aria-label="Take photo"
          >
            <span className="block h-12 w-12 rounded-full bg-white mx-auto" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ContinuousCamera;
